import { Message } from './types'

interface MemCacheItem {
  mesg: Message
  loaded?: boolean
  modified?: boolean
}

export class MessageMemCache {
  protected cache: MemCacheItem[] = []
  protected trash: Map<number, Message> = new Map<number, Message>()

  get count () {
    return this.cache.length
  }

  get minKey () {
    if (this.cache.length) {
      return this.cache[0].mesg.seq
    }
    return 0
  }

  get maxKey () {
    if (this.cache.length) {
      return this.cache[this.cache.length - 1].mesg.seq
    }
    return 0
  }

  get contents () {
    return this.cache.map((i) => i.mesg)
  }

  reset (): void {
    this.cache = []
  }

  load (mesgs: Message[]) {
    this.cache = mesgs.sort((a, b) => a.seq - b.seq).map((mesg) => ({
      mesg, loaded: true,
    }))
  }

  _read (seqOrId: number, byId = false): Message | null {
    if (byId) {
      return this.cache.find((cached) => cached.mesg.id === seqOrId)?.mesg || null
    } else {
      return this.cache.find((cached) => cached.mesg.seq === seqOrId)?.mesg || null
    }
  }

  _readBulk (seqs: number[]): Message[] {
    return this.cache
      .filter((i) => seqs.includes(i.mesg.seq))
      .map((i) => i.mesg)
      .sort((i, j) => i.seq - j.seq)
  }

  _indexOf (seq: number): number {
    return this.cache.findIndex((cached) => cached.mesg.seq === seq)
  }

  _isReservedToDelete (maxCount: number, seq: number) {
    if (this.cache.length <= maxCount) {
      return false
    }
    const idx = this._indexOf(seq)
    return 0 <= idx && maxCount < this.cache.length - idx
  }

  _trim (maxCount: number, predicate: (Message: Message, excessCount: number) => { del: boolean, stop?: boolean }) {
    const selecteds: Message[] = []
    const excess = this.cache.length - maxCount
    for (let idx = 0; idx < this.cache.length; idx++) {
      const i = this.cache[idx]
      const result = predicate(i.mesg, excess - selecteds.length)
      if (result.del) {
        selecteds.push(i.mesg)
      }
      if (result.stop) {
        break
      }
    }
    selecteds.forEach((i) => this.delete(i))
  }

  get (mesg: Message, byId = false): Message | null {
    if (byId) {
      return this._read(mesg.id, true)
    } else {
      return this._read(mesg.seq, false)
    }
  }

  getById (id: number): Message | null {
    return this._read(id, true)
  }

  put (mesg: Message): void {
    const newItem: MemCacheItem = { mesg, modified: true }
    if (this.cache.length) {
      if (mesg.seq < this.minKey) {
        // add front
        this.cache.unshift(newItem)
      } else if (this.maxKey < mesg.seq) {
        // add rear
        this.cache.push(newItem)
      } else {
        const inx = this.cache.findIndex((cached) => cached.mesg.seq >= mesg.seq)
        if (this.cache[inx].mesg.seq === mesg.seq) {
          // same: replace
          this.cache[inx] = newItem
        } else {
          // insert
          this.cache.splice(inx, 0, newItem)
        }
      }
    } else {
      this.cache = [newItem]
    }

    // since new data arrived, we can safely remove one in the trash
    if (this.trash.has(mesg.seq)) {
      this.trash.delete(mesg.seq)
    }
  }

  delete (mesg: Message): void {
    const inx = this.cache.findIndex((cached) => cached.mesg.seq === mesg.seq)
    if (inx >= 0) {
      const item = this.cache[inx]
      if (item.loaded) {
        this.trash.set(mesg.seq, mesg)
      }
      this.cache.splice(inx, 1)
    }
  }

  deleteOlds (maxCount: number): Message[] {
    const fromTrash = Array.from(this.trash.values())
    this.trash.clear()
    if (maxCount < this.cache.length) {
      const olds = this.cache.splice(0, this.cache.length - maxCount)
      return fromTrash.concat(olds.filter((item) => item.loaded).map((item) => item.mesg))
    } else {
      return fromTrash
    }
  }

  getModified (): Message[] {
    const modified = this.cache.filter((item) => item.modified)
    modified.forEach((item) => item.modified = false)
    return modified.map((item) => item.mesg)
  }

  setSaved (mesgs: Message[]): void {
    mesgs.forEach((mesg) => {
      const inx = this.cache.findIndex((cached) => cached.mesg.seq === mesg.seq)
      if (inx >= 0) {
        const item = this.cache[inx]
        item.loaded = true
      }
    })
  }
}
